export interface VideoThumbnail {
  dataUrl: string;
  blob: Blob;
  timestamp: number;
}

export interface ThumbnailSize {
  width: number;
  height: number;
}

const formatTimestamp = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Draws video frame on canvas with "cover" scaling (fills entire canvas, may crop)
 */
const drawVideoFrameWithCover = (
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  canvasWidth: number,
  canvasHeight: number
) => {
  const videoWidth = video.videoWidth;
  const videoHeight = video.videoHeight;

  const videoAspect = videoWidth / videoHeight;
  const canvasAspect = canvasWidth / canvasHeight;

  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = videoWidth;
  let sourceHeight = videoHeight;

  if (videoAspect > canvasAspect) {
    // Video is wider - crop sides
    sourceWidth = videoHeight * canvasAspect;
    sourceX = (videoWidth - sourceWidth) / 2;
  } else {
    // Video is taller - crop top/bottom
    sourceHeight = videoWidth / canvasAspect;
    sourceY = (videoHeight - sourceHeight) / 2;
  }

  ctx.drawImage(
    video,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvasWidth,
    canvasHeight
  );
};

export const generateVideoThumbnails = async (
  videoFile: File,
  targetSize?: ThumbnailSize
): Promise<VideoThumbnail[]> => {
  const video = document.createElement("video");
  video.src = URL.createObjectURL(videoFile);
  video.muted = true;
  video.preload = "metadata";

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error("Failed to load video metadata"));
  });

  const duration = video.duration;
  const timestamps = [0, duration * 0.25, duration * 0.5, duration * 0.75];

  // Use target size if provided, otherwise use video dimensions
  const canvasWidth = targetSize?.width ?? video.videoWidth;
  const canvasHeight = targetSize?.height ?? video.videoHeight;

  const thumbnails: VideoThumbnail[] = [];

  for (const time of timestamps) {
    video.currentTime = time;

    // oxlint-disable-next-line no-await-in-loop
    await new Promise<void>((resolve) => {
      video.onseeked = () => resolve();
    });

    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    if (targetSize) {
      // Scale with cover (fill canvas, crop if needed)
      drawVideoFrameWithCover(ctx, video, canvasWidth, canvasHeight);
    } else {
      // Original size - no scaling
      ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);
    }

    // oxlint-disable-next-line no-await-in-loop
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (canvasBlob) => {
          if (canvasBlob) {
            resolve(canvasBlob);
          } else {
            reject(new Error("Failed to create blob from canvas"));
          }
        },
        "image/jpeg",
        0.9
      );
    });

    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);

    thumbnails.push({
      dataUrl,
      blob,
      timestamp: time
    });
  }

  URL.revokeObjectURL(video.src);

  return thumbnails;
};

export { formatTimestamp as formatThumbnailTimestamp };
